import { useEffect, useMemo, useState } from 'react';
import FilterOptions from './components/FilterOptions';
import { checkinService, CheckinHistory, getCheckinImageSrc } from '../../api/checkin';
import {
  Search,
  Car,
  User,
  Calendar,
  Clock,
  FileText,
  Eye,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Loader2,
  AlertCircle,
  CheckCircle2,
  XCircle,
  X,
  Image as ImageIcon,
  RefreshCw,
  Filter,
  LogIn,
  LogOut,
  MapPin,
  Hourglass,
} from 'lucide-react';
import {
  PageHeader,
  SectionCard,
  CardHeader,
  CardBody,
  ModernTable,
  Thead,
  Tbody,
  Th,
  Tr,
  Td,
  Button,
  Modal,
} from '../../components/ui/SharedLayout';

const ITEMS_PER_PAGE = 20;

const HistoricoDeCheckIn = () => {
  const [history, setHistory] = useState<CheckinHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [_error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState<Record<string, string[]>>({});
  const [selectedHistory, setSelectedHistory] = useState<CheckinHistory | null>(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  useEffect(() => { if (toast) { const t = setTimeout(() => setToast(null), 2800); return () => clearTimeout(t); } }, [toast]);

  const filterCategories = useMemo(() => ({
    Ação: Array.from(new Set(history.map((item) => item.action)))
      .filter(Boolean)
      .map((action) => ({ id: action, label: action })),
  }), [history]);

  const fetchHistory = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await checkinService.getHistory();
      setHistory(data);
    } catch (_err: unknown) {
      setError('Erro ao buscar histórico de check-ins.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHistory();
  }, []);

  const filteredHistory = useMemo(() => history.filter(h => {
    const term = searchTerm.trim().toLowerCase();
    const matchesSearch =
      !term ||
      String(h.id).includes(term) ||
      String(h.checkin_id).includes(term) ||
      (h.checkin?.employee?.name || '').toLowerCase().includes(term) ||
      (h.checkin?.employee?.cpf || '').toLowerCase().includes(term) ||
      (h.checkin?.vehicle?.description || '').toLowerCase().includes(term) ||
      (h.checkin?.vehicle?.license_plate || '').toLowerCase().includes(term) ||
      (h.action || '').toLowerCase().includes(term) ||
      (h.details || '').toLowerCase().includes(term);

    const selectedActions = appliedFilters.Ação || [];
    const matchesAction =
      selectedActions.length === 0 || selectedActions.includes(h.action);

    return matchesSearch && matchesAction;
  }), [history, searchTerm, appliedFilters]);

  const totalPaginas = useMemo(
    () => Math.max(1, Math.ceil(filteredHistory.length / ITEMS_PER_PAGE)),
    [filteredHistory.length],
  );

  useEffect(() => {
    if (currentPage > totalPaginas) setCurrentPage(1);
  }, [currentPage, totalPaginas]);

  const { historyPaginados, pageRange } = useMemo(() => {
    const total = filteredHistory.length;
    const safePage = Math.min(currentPage, totalPaginas);
    const startIndex = (safePage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return {
      historyPaginados: filteredHistory.slice(startIndex, endIndex),
      pageRange: {
        start: total === 0 ? 0 : startIndex + 1,
        end: Math.min(endIndex, total),
        total,
      },
    };
  }, [filteredHistory, currentPage, totalPaginas]);

  const getPageNumbers = useMemo(() => {
    const pages: (number | '...')[] = [];
    const maxVisible = 5;
    if (totalPaginas <= maxVisible + 2) {
      for (let i = 1; i <= totalPaginas; i++) pages.push(i);
      return pages;
    }
    pages.push(1);
    const left = Math.max(2, currentPage - 1);
    const right = Math.min(totalPaginas - 1, currentPage + 1);
    if (left > 2) pages.push('...');
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < totalPaginas - 1) pages.push('...');
    pages.push(totalPaginas);
    return pages;
  }, [currentPage, totalPaginas]);

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    try { return new Date(dateStr).toLocaleDateString('pt-BR'); }
    catch { return '-'; }
  };

  const formatDateTime = (dateStr?: string) => {
    if (!dateStr) return '-';
    try { return new Date(dateStr).toLocaleString('pt-BR'); }
    catch { return '-'; }
  };

  const formatTime = (dateStr?: string) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    } catch { return '-'; }
  };

  const getActionBadgeClasses = (action: string) => {
    const normalized = (action || '').toLowerCase();
    if (normalized.includes('cri') || normalized === 'criado' || normalized === 'criação') return 'bg-blue-50 text-blue-800 ring-blue-600/20';
    if (normalized.includes('atualiz') || normalized === 'atualizado') return 'bg-amber-50 text-amber-800 ring-amber-600/20';
    if (normalized.includes('finaliz') || normalized.includes('conclu') || normalized === 'finalizado') return 'bg-emerald-50 text-emerald-800 ring-emerald-600/20';
    if (normalized.includes('cancel') || normalized.includes('rejeit')) return 'bg-rose-50 text-rose-800 ring-rose-600/20';
    if (normalized.includes('check') && normalized.includes('out')) return 'bg-purple-50 text-purple-800 ring-purple-600/20';
    if (normalized.includes('check') || normalized.includes('in')) return 'bg-sky-50 text-sky-800 ring-sky-600/20';
    return 'bg-slate-50 text-slate-700 ring-slate-600/20';
  };

  const handleViewHistory = (h: CheckinHistory) => {
    setSelectedHistory(h);
    setIsViewModalOpen(true);
  };

  const closeViewModal = () => {
    setSelectedHistory(null);
    setIsViewModalOpen(false);
  };

  const handleImageClick = (url: string) => setSelectedImage(url);
  const handleCloseImageModal = () => setSelectedImage(null);

  return (
    <>
      <PageHeader
        title="Histórico de Check-In"
        description="Trilha de auditoria · Histórico completo de check-in — acompanhe todas as entradas, saídas e atualizações de uso de veículos."
        hideTitleInBody
      />

      {toast && (
        <div className={`fixed top-5 right-5 z-[100] inline-flex items-center gap-2.5 rounded-xl px-4 py-3 shadow-[0_10px_30px_-12px_rgba(15,23,42,0.35)] border animate-[fadeIn_0.2s_ease-out] ${toast.type === 'success' ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-rose-50 border-rose-200 text-rose-800'}`}>
          {toast.type === 'success' ? <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" /> : <XCircle className="h-5 w-5 text-rose-600 shrink-0" />}
          <span className="text-[13px] font-semibold leading-tight">{toast.message}</span>
        </div>
      )}

      <div className="flex flex-col gap-5">
        {_error && (
          <div className="inline-flex items-start gap-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-rose-600" />
            <div className="flex flex-col gap-0.5">
              <span className="text-[13px] font-bold leading-tight">Não foi possível carregar</span>
              <span className="text-[12.5px] font-medium leading-tight text-rose-700/90">{_error}</span>
            </div>
          </div>
        )}

        <SectionCard>
          <CardHeader>
            <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="text-[15px] font-bold tracking-tight text-slate-900">Todos os registros</h2>
                <p className="mt-0.5 text-[12.5px] font-medium leading-tight text-slate-500">
                  Criações, atualizações e finalizações de check-in
                </p>
              </div>
              <div className="flex items-center gap-2 w-full sm:w-auto sm:flex-1 sm:max-w-xl">
                <div className="relative flex-1 min-w-0">
                  <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <input
                    value={searchTerm}
                    onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                    placeholder="Buscar ID, funcionário, CPF, veículo, placa..."
                    className="block w-full rounded-lg border border-slate-200 bg-white px-3 py-2 pl-10 text-sm text-slate-900 placeholder:text-slate-400 shadow-[0_1px_2px_0_rgba(15,23,42,0.02)] transition-all duration-150 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 h-10"
                  />
                </div>
                <Button
                  variant="secondary"
                  size="lg"
                  onClick={() => setIsFilterOpen(true)}
                  className="h-10 shrink-0"
                >
                  <Filter className="h-4 w-4 stroke-[1.9]" />
                  Filtros
                </Button>
                <Button
                  variant="secondary"
                  size="lg"
                  onClick={() => { setSubmitting(true); void fetchHistory().finally(() => setSubmitting(false)); }}
                  title="Atualizar informações"
                  className="h-10 shrink-0"
                  disabled={submitting}
                >
                  <RefreshCw className={`h-4 w-4 stroke-[1.9] ${submitting ? 'animate-spin' : ''}`} />
                  Atualizar
                </Button>
              </div>
            </div>
          </CardHeader>

          <CardBody>
            {loading && history.length === 0 ? (
              <div className="flex items-center justify-center gap-2.5 py-16">
                <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                <span className="text-[13.5px] font-semibold text-slate-600">Carregando histórico...</span>
              </div>
            ) : (
              <ModernTable className="table-fixed layout-fixed">
                <Thead>
                  <Tr>
                    <Th className="w-[12%] min-w-[160px] max-w-[200px]">Data</Th>
                    <Th className="w-[10%] min-w-[140px] max-w-[180px]">Ação</Th>
                    <Th className="w-[17%] min-w-[220px] max-w-[280px]">Funcionário</Th>
                    <Th className="w-[17%] min-w-[220px] max-w-[280px]">Veículo</Th>
                    <Th className="w-[10%] min-w-[110px] max-w-[140px]">Check-in</Th>
                    <Th className="w-[10%] min-w-[110px] max-w-[140px]">Check-out</Th>
                    <Th className="w-[14%] min-w-[180px] max-w-[240px]">Detalhes</Th>
                    <Th className="w-[10%] min-w-[100px] max-w-[140px]">Duração</Th>
                    <Th className="w-[6%] min-w-[80px] max-w-[100px]">Ações</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {historyPaginados.length === 0 ? (
                    <Tr>
                      <Td colSpan={9}>
                        <div className="flex flex-col items-center justify-center gap-2 py-14 text-center">
                          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100 text-slate-400">
                            <LogIn className="h-7 w-7 stroke-[1.8]" />
                          </div>
                          <div className="text-[14px] font-bold text-slate-700">Nenhum check-in encontrado</div>
                          <p className="max-w-sm text-[12.5px] font-medium leading-relaxed text-slate-500">
                            {searchTerm ? 'Nenhum resultado para a busca atual. Tente outro termo.' : 'Não há registros de check-in no sistema.'}
                          </p>
                        </div>
                      </Td>
                    </Tr>
                  ) : (
                    historyPaginados.map((h) => {
                      const checkinImgObj = h.checkin?.images?.find(img => img.type === 'CHECKIN');
                      const checkoutImgObj = h.checkin?.images?.find(img => img.type === 'CHECKOUT');
                      const checkinSrc = getCheckinImageSrc(checkinImgObj);
                      const checkoutSrc = getCheckinImageSrc(checkoutImgObj);
                      const duration = h.checkin?.duration;
                      return (
                        <Tr key={h.id}>
                          <Td>
                            <div>
                              <div className="inline-flex items-center gap-1.5 text-[13px] font-medium text-slate-700 tabular-nums">
                                <Calendar className="h-[15px] w-[15px] text-slate-400" />
                                {formatDate(h.created_at)}
                              </div>
                              <div className="mt-1 inline-flex items-center gap-1.5 text-[11.5px] font-medium text-slate-500 tabular-nums">
                                <Clock className="h-[13px] w-[13px] text-slate-400" />
                                {formatTime(h.created_at)}
                              </div>
                            </div>
                          </Td>
                          <Td>
                            <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11.5px] font-bold ring-1 ring-inset ${getActionBadgeClasses(h.action)}`}>
                              {h.action}
                            </span>
                          </Td>
                          <Td>
                            <div className="min-w-0 w-full max-w-[280px] overflow-hidden">
                              <div className="flex items-start gap-2.5">
                                <div className="mt-0.5 shrink-0 flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200">
                                  <User className="h-[18px] w-[18px] stroke-[1.9]" />
                                </div>
                                <div className="min-w-0 flex-1">
                                  <span className="block truncate text-[13.5px] font-semibold leading-tight text-slate-900" title={h.checkin?.employee?.name || '-'}>
                                    {h.checkin?.employee?.name || '-'}
                                  </span>
                                  {h.checkin?.employee?.cpf && (
                                    <span className="block truncate text-[11.5px] font-medium leading-tight text-slate-500 mt-0.5" title={h.checkin.employee.cpf}>
                                      {h.checkin.employee.cpf}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </Td>
                          <Td>
                            <div className="min-w-0 w-full max-w-[280px] overflow-hidden">
                              <div className="flex items-start gap-2.5">
                                <div className="mt-0.5 shrink-0 flex h-9 w-9 items-center justify-center rounded-xl bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-200">
                                  <Car className="h-[18px] w-[18px] stroke-[1.9]" />
                                </div>
                                <div className="min-w-0 flex-1">
                                  <span className="block truncate text-[13.5px] font-semibold leading-tight text-slate-900" title={h.checkin?.vehicle?.description || '-'}>
                                    {h.checkin?.vehicle?.description || '-'}
                                  </span>
                                  {h.checkin?.vehicle?.license_plate && (
                                    <span className="block truncate text-[11.5px] font-medium leading-tight text-slate-500 mt-0.5" title={h.checkin.vehicle.license_plate}>
                                      Placa: {h.checkin.vehicle.license_plate}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </Td>
                          <Td>
                            <div className="flex justify-center">
                              {checkinSrc ? (
                                <button
                                  onClick={() => handleImageClick(checkinSrc)}
                                  className="group relative h-11 w-11 shrink-0 overflow-hidden rounded-lg border border-slate-200 bg-slate-50 ring-1 ring-inset ring-slate-200 transition-all hover:ring-sky-300 hover:shadow-md"
                                  title="Ver foto de check-in"
                                >
                                  <div className="absolute top-0.5 left-0.5 z-10 flex h-4 w-4 items-center justify-center rounded bg-sky-600 text-white ring-1 ring-white/80 shadow">
                                    <LogIn className="h-2.5 w-2.5 stroke-[2.2]" />
                                  </div>
                                  <img
                                    src={checkinSrc}
                                    alt="Check-in"
                                    className="h-full w-full object-cover transition-transform group-hover:scale-105"
                                  />
                                </button>
                              ) : (
                                <div className="flex h-11 w-11 items-center justify-center rounded-lg border border-slate-200 bg-slate-50 text-slate-300">
                                  <ImageIcon className="h-5 w-5 stroke-[1.8]" />
                                </div>
                              )}
                            </div>
                          </Td>
                          <Td>
                            <div className="flex justify-center">
                              {checkoutSrc ? (
                                <button
                                  onClick={() => handleImageClick(checkoutSrc)}
                                  className="group relative h-11 w-11 shrink-0 overflow-hidden rounded-lg border border-slate-200 bg-slate-50 ring-1 ring-inset ring-slate-200 transition-all hover:ring-purple-300 hover:shadow-md"
                                  title="Ver foto de check-out"
                                >
                                  <div className="absolute top-0.5 left-0.5 z-10 flex h-4 w-4 items-center justify-center rounded bg-purple-600 text-white ring-1 ring-white/80 shadow">
                                    <LogOut className="h-2.5 w-2.5 stroke-[2.2]" />
                                  </div>
                                  <img
                                    src={checkoutSrc}
                                    alt="Check-out"
                                    className="h-full w-full object-cover transition-transform group-hover:scale-105"
                                  />
                                </button>
                              ) : (
                                <div className="flex h-11 w-11 items-center justify-center rounded-lg border border-slate-200 bg-slate-50 text-slate-300">
                                  <ImageIcon className="h-5 w-5 stroke-[1.8]" />
                                </div>
                              )}
                            </div>
                          </Td>
                          <Td>
                            <div className="min-w-0 w-full max-w-[240px] overflow-hidden">
                              <div className="flex items-start gap-2.5">
                                <div className="mt-0.5 shrink-0 flex h-9 w-9 items-center justify-center rounded-xl bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-200">
                                  <FileText className="h-[18px] w-[18px] stroke-[1.9]" />
                                </div>
                                <div className="min-w-0 flex-1">
                                  <span className="block truncate text-[13px] font-medium leading-tight text-slate-800" title={h.details}>
                                    {h.details || '-'}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </Td>
                          <Td>
                            <div>
                              {duration !== null && duration !== undefined ? (
                                <div className="inline-flex items-center gap-1.5">
                                  <Hourglass className="h-[15px] w-[15px] text-indigo-500 stroke-[1.9]" />
                                  <span className="text-[13px] font-bold text-indigo-700 tabular-nums">
                                    {duration} <span className="text-[11px] font-semibold text-indigo-600">{h.details?.includes('segundos') ? 's' : 'min'}</span>
                                  </span>
                                </div>
                              ) : (
                                <span className="text-[12.5px] font-medium text-slate-400">-</span>
                              )}
                            </div>
                          </Td>
                          <Td>
                            <div className="flex items-center justify-center gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleViewHistory(h)}
                                title="Visualizar"
                                className="h-8 w-8 rounded-lg text-slate-500 hover:text-blue-700 hover:bg-blue-50 hover:ring-blue-200"
                              >
                                <Eye className="h-4 w-4 stroke-[1.9]" />
                              </Button>
                            </div>
                          </Td>
                        </Tr>
                      );
                    })
                  )}
                </Tbody>
              </ModernTable>
            )}

            {pageRange.total > 0 ? (
              <div className="mt-4 flex flex-col gap-3 border-t border-slate-200/80 pt-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="text-[12.5px] font-medium leading-tight text-slate-500">
                  Mostrando{' '}
                  <span className="font-bold tabular-nums text-slate-800">{pageRange.start}</span>
                  {'–'}{' '}
                  <span className="font-bold tabular-nums text-slate-800">{pageRange.end}</span>
                  {' de '}
                  <span className="font-bold tabular-nums text-slate-800">{pageRange.total}</span>
                  {' registro(s)'}
                </div>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                    className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-blue-400/40 focus:ring-offset-1"
                    aria-label="Primeira página"
                  >
                    <ChevronsLeft className="h-4 w-4 stroke-[1.8]" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-blue-400/40 focus:ring-offset-1"
                    aria-label="Página anterior"
                  >
                    <ChevronLeft className="h-4 w-4 stroke-[1.8]" />
                  </button>
                  <div className="inline-flex items-center gap-1 mx-1">
                    {getPageNumbers.map((n, idx) =>
                      n === '...' ? (
                        <span key={`dots-${idx}`} className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 text-[13px]">…</span>
                      ) : (
                        <button
                          key={n}
                          type="button"
                          onClick={() => setCurrentPage(n)}
                          disabled={submitting}
                          className={`inline-flex h-9 min-w-[36px] cursor-pointer items-center justify-center rounded-lg px-2.5 text-[13px] font-semibold transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-blue-400/40 focus:ring-offset-1 ${n === currentPage ? 'bg-blue-600 text-white shadow-[0_4px_14px_-4px_rgba(37,99,235,0.55)]' : 'border border-slate-200 bg-white text-slate-700 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700'}`}
                        >
                          {n}
                        </button>
                      ),
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => setCurrentPage((p) => Math.min(totalPaginas, p + 1))}
                    disabled={currentPage === totalPaginas}
                    className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-blue-400/40 focus:ring-offset-1"
                    aria-label="Próxima página"
                  >
                    <ChevronRight className="h-4 w-4 stroke-[1.8]" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setCurrentPage(totalPaginas)}
                    disabled={currentPage === totalPaginas}
                    className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-blue-400/40 focus:ring-offset-1"
                    aria-label="Última página"
                  >
                    <ChevronsRight className="h-4 w-4 stroke-[1.8]" />
                  </button>
                </div>
              </div>
            ) : null}
          </CardBody>
        </SectionCard>
      </div>

      {/* Image Preview Modal (Lightbox) */}
      {selectedImage && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-900/75 backdrop-blur-sm p-4 sm:p-6"
          onClick={handleCloseImageModal}
        >
          <div
            className="relative max-w-5xl max-h-[90vh] w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={handleCloseImageModal}
              className="absolute -top-3 -right-3 flex h-10 w-10 items-center justify-center rounded-full bg-white text-slate-600 shadow-lg ring-1 ring-slate-200 transition-all hover:bg-slate-100 hover:text-slate-900 z-10"
              aria-label="Fechar"
            >
              <X className="h-5 w-5 stroke-[2]" />
            </button>
            <div className="overflow-hidden rounded-2xl border border-white/20 shadow-2xl bg-black/20">
              <img
                src={selectedImage}
                alt="Visualização completa"
                className="w-full max-h-[85vh] object-contain"
              />
            </div>
          </div>
        </div>
      )}

      {isViewModalOpen && selectedHistory && (
        <Modal
          onClose={closeViewModal}
          maxWidthClass="max-w-4xl"
          title={
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-sky-500 via-indigo-500 to-indigo-700 text-white shadow-[0_6px_16px_-8px_rgba(14,165,233,0.6)]">
                <LogIn className="h-5 w-5 stroke-[1.9]" />
              </div>
              <div>
                <div className="text-[16px] font-bold tracking-tight text-slate-900 leading-none">
                  Detalhes do Check-In
                </div>
                <div className="mt-1 text-[12px] font-medium text-slate-500 leading-tight">
                  Registro #{selectedHistory.id} • Check-in #{selectedHistory.checkin_id}
                </div>
              </div>
            </div>
          }
          footer={
            <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:items-center">
              <button
                type="button"
                onClick={closeViewModal}
                className="inline-flex h-11 items-center justify-center gap-1.5 rounded-xl border border-slate-200 bg-white px-5 text-[13px] font-semibold text-slate-700 transition-all hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400/30"
              >
                Fechar
              </button>
            </div>
          }
        >
          <div className="space-y-6">
            <div>
              <div className="mb-2 flex items-center gap-2">
                <div className="h-6 w-1 shrink-0 rounded-full bg-gradient-to-b from-blue-500 to-indigo-600" />
                <h3 className="text-[13px] font-bold uppercase tracking-[0.14em] text-blue-700">Dados do Registro</h3>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50/40 p-4 sm:p-5 space-y-4">
                <div className="grid gap-4 md:grid-cols-4">
                  <div className="flex flex-col gap-1">
                    <span className="text-xs font-semibold text-slate-500">ID Registro</span>
                    <p className="text-sm text-slate-900 font-medium tabular-nums">#{selectedHistory.id}</p>
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="text-xs font-semibold text-slate-500">ID Check-in</span>
                    <p className="text-sm text-slate-900 font-medium tabular-nums">#{selectedHistory.checkin_id}</p>
                  </div>
                  <div className="flex flex-col gap-1 md:col-span-2">
                    <span className="text-xs font-semibold text-slate-500">Ação</span>
                    <div className="mt-0.5">
                      <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11.5px] font-bold ring-1 ring-inset ${getActionBadgeClasses(selectedHistory.action)}`}>
                        {selectedHistory.action}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="flex flex-col gap-1">
                    <span className="text-xs font-semibold text-slate-500">Data/Hora do Registro</span>
                    <p className="text-sm text-slate-900 font-medium tabular-nums">{formatDateTime(selectedHistory.created_at)}</p>
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="text-xs font-semibold text-slate-500">Duração</span>
                    <div>
                      {selectedHistory.checkin?.duration !== null && selectedHistory.checkin?.duration !== undefined ? (
                        <p className="text-sm font-bold text-indigo-700 tabular-nums inline-flex items-center gap-1.5">
                          <Hourglass className="h-4 w-4 text-indigo-600 stroke-[1.9]" />
                          {selectedHistory.checkin.duration} <span className="text-[11.5px] font-semibold text-indigo-600">{selectedHistory.details?.includes('segundos') ? 'segundos' : 'minutos'}</span>
                        </p>
                      ) : <p className="text-sm font-medium text-slate-500">-</p>}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <div className="mb-2 flex items-center gap-2">
                <div className="h-6 w-1 shrink-0 rounded-full bg-gradient-to-b from-emerald-500 to-teal-600" />
                <h3 className="text-[13px] font-bold uppercase tracking-[0.14em] text-emerald-700">Funcionário e Veículo</h3>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="flex items-start gap-2.5">
                    <div className="mt-0.5 shrink-0 flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200">
                      <User className="h-5 w-5 stroke-[1.9]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-semibold text-slate-500 mb-1">Funcionário</div>
                      <p className="text-sm text-slate-900 font-semibold leading-tight">
                        {selectedHistory.checkin?.employee?.name || '-'}
                      </p>
                      {selectedHistory.checkin?.employee?.cpf && (
                        <p className="text-[11.5px] font-semibold text-slate-500 mt-0.5 leading-tight">
                          {selectedHistory.checkin.employee.cpf}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-start gap-2.5">
                    <div className="mt-0.5 shrink-0 flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-200">
                      <Car className="h-5 w-5 stroke-[1.9]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-semibold text-slate-500 mb-1">Veículo</div>
                      <p className="text-sm text-slate-900 font-semibold leading-tight">
                        {selectedHistory.checkin?.vehicle?.description || '-'}
                      </p>
                      {selectedHistory.checkin?.vehicle?.license_plate && (
                        <p className="text-[11.5px] font-semibold text-slate-500 mt-0.5 leading-tight">
                          Placa: {selectedHistory.checkin.vehicle.license_plate}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <div className="mb-2 flex items-center gap-2">
                <div className="h-6 w-1 shrink-0 rounded-full bg-gradient-to-b from-amber-500 to-orange-600" />
                <h3 className="text-[13px] font-bold uppercase tracking-[0.14em] text-amber-700">Fotos do Check-in / Check-out</h3>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="flex flex-col gap-2">
                    <div className="inline-flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-wider text-sky-700">
                      <LogIn className="h-3.5 w-3.5 stroke-[2]" />
                      Check-in
                    </div>
                    {(() => {
                      const checkinImgObj = selectedHistory.checkin?.images?.find(img => img.type === 'CHECKIN');
                      const checkinSrc = getCheckinImageSrc(checkinImgObj);
                      return checkinSrc ? (
                        <button
                          onClick={() => handleImageClick(checkinSrc)}
                          className="group block w-full overflow-hidden rounded-xl border border-slate-200 transition-all hover:ring-2 hover:ring-sky-300"
                        >
                          <img
                            src={checkinSrc}
                            alt="Check-in"
                            className="w-full h-auto max-h-80 object-cover transition-transform group-hover:scale-[1.02]"
                          />
                        </button>
                      ) : (
                        <div className="flex flex-col items-center justify-center gap-2 rounded-xl bg-slate-50 py-12 text-slate-400 border border-dashed border-slate-200">
                          <ImageIcon className="h-10 w-10 stroke-[1.8]" />
                          <span className="text-[12.5px] font-semibold">Sem foto de check-in.</span>
                        </div>
                      );
                    })()}
                  </div>
                  <div className="flex flex-col gap-2">
                    <div className="inline-flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-wider text-purple-700">
                      <LogOut className="h-3.5 w-3.5 stroke-[2]" />
                      Check-out
                    </div>
                    {(() => {
                      const checkoutImgObj = selectedHistory.checkin?.images?.find(img => img.type === 'CHECKOUT');
                      const checkoutSrc = getCheckinImageSrc(checkoutImgObj);
                      return checkoutSrc ? (
                        <button
                          onClick={() => handleImageClick(checkoutSrc)}
                          className="group block w-full overflow-hidden rounded-xl border border-slate-200 transition-all hover:ring-2 hover:ring-purple-300"
                        >
                          <img
                            src={checkoutSrc}
                            alt="Check-out"
                            className="w-full h-auto max-h-80 object-cover transition-transform group-hover:scale-[1.02]"
                          />
                        </button>
                      ) : (
                        <div className="flex flex-col items-center justify-center gap-2 rounded-xl bg-slate-50 py-12 text-slate-400 border border-dashed border-slate-200">
                          <ImageIcon className="h-10 w-10 stroke-[1.8]" />
                          <span className="text-[12.5px] font-semibold">Sem foto de check-out.</span>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>
            </div>

            <div>
              <div className="mb-2 flex items-center gap-2">
                <div className="h-6 w-1 shrink-0 rounded-full bg-gradient-to-b from-purple-500 to-purple-600" />
                <h3 className="text-[13px] font-bold uppercase tracking-[0.14em] text-purple-700">Viagem e Observações</h3>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50/40 p-4 sm:p-5 space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="flex flex-col gap-1">
                    <div className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500">
                      <MapPin className="h-3.5 w-3.5 text-indigo-500" />
                      Destino da viagem
                    </div>
                    <p className="text-sm text-slate-900 font-medium">
                      {selectedHistory.checkin?.vehicle?.trips?.[0]?.destination || '-'}
                    </p>
                  </div>
                  <div className="flex flex-col gap-1">
                    <div className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500">
                      <Calendar className="h-3.5 w-3.5 text-indigo-500" />
                      Data da viagem
                    </div>
                    <p className="text-sm text-slate-900 font-medium tabular-nums">
                      {selectedHistory.checkin?.vehicle?.trips?.[0]?.start_date
                        ? formatDate(selectedHistory.checkin.vehicle.trips[0].start_date)
                        : '-'}
                    </p>
                  </div>
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-xs font-semibold text-slate-500">Notas</span>
                  <p className="text-sm text-slate-900 font-medium whitespace-pre-wrap leading-relaxed">
                    {selectedHistory.checkin?.notes ?? '-'}
                  </p>
                </div>
                <div className="flex flex-col gap-1 pt-3 border-t border-slate-200/70">
                  <span className="text-xs font-semibold text-slate-500">Detalhes da ação</span>
                  <p className="text-sm text-slate-900 font-medium whitespace-pre-wrap leading-relaxed">
                    {selectedHistory.details || '-'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </Modal>
      )}

      <FilterOptions
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        onApplyFilter={(f) => { setAppliedFilters(f); setCurrentPage(1); }}
        filterCategories={filterCategories}
        initialFilters={appliedFilters}
      />
    </>
  );
};

export default HistoricoDeCheckIn;
